import type { HubEvent } from '@aiai/shared';

export type TvCue = { kind: 'distress' | 'launch' | 'crash' | 'start' | 'meteor'; projectId: string; at: number };

export const TV_PRIORITY: Record<TvCue['kind'], number> = { distress: 6, launch: 5, crash: 4, start: 3, meteor: 2 };
export const SHOT_MS = 8000;
export const MIN_SHOT_MS = 5000;
export const TOUR_MS = 15000;
/** A pending cue older than this is dropped unseen (two full shots). */
export const CUE_TTL_MS = 2 * SHOT_MS;

/** Idle tour shots have priority 1, the fit view 0. */
const TOUR_PRIORITY = 1;

export interface Shot {
  /** null = the fit view (whole universe). */
  projectId: string | null;
  priority: number;
  startedAt: number;
}

export interface DirectorInput {
  current: Shot;
  pending: TvCue[];
  /** Project ids that currently have a run in flight, in tour order. */
  working: string[];
  planets: Set<string>;
  lastTourIndex: number;
  now: number;
}

/**
 * Pure TV director: decides which planet the camera shows next.
 *
 * Idle tour (no cue cuts in): once the current shot has expired (SHOT_MS),
 * - if the current shot is itself a tour stop or the fit view, the director only moves on once
 *   it has run TOUR_MS, so the tour advances every 15 s;
 * - if an event shot expired, it goes straight to the next working planet (tour stop);
 * - with nothing working it returns to fit (and stays there unchanged).
 * Tour stops for planets that no longer exist are skipped.
 */
export function nextShot(input: DirectorInput): { shot: Shot; pending: TvCue[]; lastTourIndex: number } {
  const { current, working, planets, lastTourIndex, now } = input;
  const elapsed = now - current.startedAt;
  const expired = elapsed >= SHOT_MS;

  // Drop cues for planets that no longer exist and cues too old to still be news (cue.at is on the
  // same Date.now() clock as `now`); newest cue per (kind, project) wins.
  const newest = new Map<string, TvCue>();
  for (const c of input.pending) {
    if (!planets.has(c.projectId) || now - c.at > CUE_TTL_MS) continue;
    const key = `${c.kind}:${c.projectId}`;
    const prev = newest.get(key);
    if (!prev || c.at >= prev.at) newest.set(key, c);
  }
  const pending = [...newest.values()];

  let best: TvCue | null = null;
  for (const c of pending) {
    if (
      !best ||
      TV_PRIORITY[c.kind] > TV_PRIORITY[best.kind] ||
      (TV_PRIORITY[c.kind] === TV_PRIORITY[best.kind] && c.at > best.at)
    ) {
      best = c;
    }
  }

  if (best) {
    const cut = best;
    const p = TV_PRIORITY[cut.kind];
    if (p > current.priority || (elapsed >= MIN_SHOT_MS && p >= current.priority) || expired) {
      return {
        shot: { projectId: cut.projectId, priority: p, startedAt: now },
        pending: pending.filter((c) => !(c.projectId === cut.projectId && c.kind === cut.kind)),
        lastTourIndex,
      };
    }
  }

  if (!expired) return { shot: current, pending, lastTourIndex };

  const idleCurrent = current.projectId === null || current.priority === TOUR_PRIORITY;
  const stops = working.filter((id) => planets.has(id));
  if (stops.length > 0) {
    if (idleCurrent && elapsed < TOUR_MS) return { shot: current, pending, lastTourIndex };
    // Anchor on the planet being shown: next stop after it in the CURRENT order (wrapping), so a
    // changing working set never skips or repeats. If it is not a stop (fit, event shot, stopped
    // working), lastTourIndex is only a hint, and the shown planet is never re-picked.
    const at = current.projectId === null ? -1 : stops.indexOf(current.projectId);
    if (at >= 0 && stops.length === 1) return { shot: current, pending, lastTourIndex: 0 };
    // A tour stop that just stopped working vacated slot lastTourIndex, so its successor now sits
    // there; for fit/event shots the hint is the last stop shown, so advance one.
    const hint = Math.max(lastTourIndex, -1) + (current.priority === TOUR_PRIORITY ? 0 : 1);
    const idx = at >= 0 ? (at + 1) % stops.length : hint % stops.length;
    return {
      shot: { projectId: stops[idx], priority: TOUR_PRIORITY, startedAt: now },
      pending,
      lastTourIndex: idx,
    };
  }

  if (current.projectId === null) return { shot: current, pending, lastTourIndex };
  return { shot: { projectId: null, priority: 0, startedAt: now }, pending, lastTourIndex };
}

export function cueFromHubEvent(e: HubEvent, now: number): TvCue | null {
  const make = (kind: TvCue['kind']): TvCue => ({ kind, projectId: e.projectId, at: now });
  switch (e.type) {
    case 'error.caught':
      return make('distress');
    case 'pr.merged':
      return make('launch');
    case 'run.started':
      return make('start');
    case 'issue.arrived':
      return make('meteor');
    case 'run.line':
      return e.kind === 'gate' && (e.text ?? '').startsWith('Gate failed') ? make('crash') : null;
    default:
      return null;
  }
}
